// @ts-nocheck
/**
 * Marketing Landing Page
 * Built with IBM Carbon Design System components
 */

import React from 'react';
import {
  Button,
  ButtonSet,
  Grid,
  Column,
  Tile,
  ClickableTile,
  Tag,
  ContentSwitcher,
  Switch,
  Breadcrumb,
  BreadcrumbItem,
  StructuredList,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListRow,
  StructuredListCell,
  StructuredListBody,
  Accordion,
  AccordionItem,
  Link,
  Theme,
} from '@carbon/react';
import {
  ArrowRight,
  Calendar,
  CheckmarkFilled,
  Education,
  Analytics,
  Collaborate,
  Security,
  Time,
  UserMultiple,
  ChartLineData,
  Badge,
  Star,
  Book,
  PlayFilledAlt,
  Menu,
  Close,
  ChevronDown,
} from '@carbon/icons-react';
import { MarketingVariant } from '../../routes/MarketingRouter';
import MarketingHeader from '../../components/marketing/MarketingHeader';
import MarketingFooter from '../../components/marketing/MarketingFooter';
import EducationalCalculatorHub from '../../components/marketing/EducationalCalculatorHub';
import ConversionBreadcrumb from '../../components/marketing/ConversionBreadcrumb';
import {
  initMarketingPerformanceOptimizations,
  trackConversionFunnelPerformance,
} from '../../utils/marketingPerformance';
import './MarketingLandingPage.scss';

interface MarketingLandingPageProps {
  variant?: MarketingVariant;
}

interface Feature {
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
}

interface Statistic {
  value: string;
  label: string;
  trend?: string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  organization: string;
  rating: number;
}

const MarketingLandingPage: React.FC<MarketingLandingPageProps> = ({ variant = 'default' }) => {
  // Initialize performance optimizations on mount
  React.useEffect(() => {
    const startTime = performance.now();
    initMarketingPerformanceOptimizations();
    trackConversionFunnelPerformance('landing_page_load', startTime);
  }, []);

  // Track variant-specific performance
  React.useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'marketing_variant_view', {
        event_category: 'Marketing',
        event_label: variant,
        custom_parameters: {
          variant,
          page: 'landing',
        },
      });
    }
  }, [variant]);
  const getHeroContent = () => {
    switch (variant) {
      case 'educator':
        return {
          headline: 'Transform Your Classroom with AI Teaching Tools',
          subheadline:
            'Join 15,000+ teachers using Sigma08 to personalize instruction, automate grading, and boost student engagement with AI-powered classroom tools.',
          primaryCTA: 'Start Teaching with AI',
          secondaryCTA: 'Watch Teacher Demo',
        };
      case 'parent':
        return {
          headline: "Stay Connected to Your Child's Learning Journey",
          subheadline:
            "Stay informed about your child's progress with real-time insights, homework support, and secure communication with teachers.",
          primaryCTA: 'Connect with Your Child',
          secondaryCTA: 'See Parent Features',
        };
      case 'administrator':
        return {
          headline: 'Drive District-Wide Educational Excellence',
          subheadline:
            'Improve district-wide outcomes with comprehensive analytics, streamlined operations, and evidence-based decision making tools.',
          primaryCTA: 'Schedule District Demo',
          secondaryCTA: 'View Case Studies',
        };
      default:
        return {
          headline: 'Transform Education with AI-Powered Learning',
          subheadline:
            'Join 10,000+ educators already using Sigma08 to improve learning outcomes and streamline educational workflows with AI-powered tools.',
          primaryCTA: 'Start Free 30-Day Trial',
          secondaryCTA: 'Watch Demo',
        };
    }
  };

  const getFeatures = (): Feature[] => {
    switch (variant) {
      case 'educator':
        return [
          {
            icon: <Education size={32} />,
            title: 'AI Lesson Planning',
            description:
              'Generate personalized lesson plans and assessments in minutes, aligned to curriculum standards.',
            badge: 'Save 5hrs/week',
          },
          {
            icon: <Analytics size={32} />,
            title: 'Student Analytics',
            description: 'Track individual progress with detailed insights to identify learning gaps and strengths.',
            badge: 'Real-time',
          },
          {
            icon: <Time size={32} />,
            title: 'Automated Grading',
            description: 'Save hours weekly with intelligent grading and instant feedback for students.',
            badge: 'AI-powered',
          },
          {
            icon: <Collaborate size={32} />,
            title: 'Parent Communication',
            description: 'Streamline parent-teacher communication with automated updates and messaging.',
            badge: 'Secure',
          },
        ];
      case 'parent':
        return [
          {
            icon: <ChartLineData size={32} />,
            title: 'Progress Tracking',
            description: 'Real-time updates on homework, grades, and learning milestones delivered to your phone.',
            badge: 'Live updates',
          },
          {
            icon: <Collaborate size={32} />,
            title: 'Teacher Communication',
            description: 'Secure messaging with teachers and instant notifications about school activities.',
            badge: 'FERPA compliant',
          },
          {
            icon: <Education size={32} />,
            title: 'Home Learning Support',
            description: 'AI tutoring assistance and study resources to support learning at home.',
            badge: '24/7 available',
          },
          {
            icon: <UserMultiple size={32} />,
            title: 'Family Dashboard',
            description: "Manage multiple children's education from a single, intuitive dashboard.",
            badge: 'Multi-child',
          },
        ];
      case 'administrator':
        return [
          {
            icon: <Analytics size={32} />,
            title: 'District Dashboards',
            description: 'Comprehensive analytics across schools, teachers, and students for data-driven decisions.',
            badge: 'Enterprise',
          },
          {
            icon: <Security size={32} />,
            title: 'Compliance Management',
            description: 'Automated FERPA, COPPA, and state compliance reporting with audit trails.',
            badge: 'SOC 2 Type II',
          },
          {
            icon: <ChartLineData size={32} />,
            title: 'Resource Optimization',
            description: 'Identify resource needs, optimize staffing, and improve operational efficiency.',
            badge: 'Cost-effective',
          },
          {
            icon: <Badge size={32} />,
            title: 'Performance Metrics',
            description: 'Track KPIs, benchmark against districts, and demonstrate ROI.',
            badge: 'Evidence-based',
          },
        ];
      default:
        return [
          {
            icon: <Education size={32} />,
            title: 'Personalized Learning',
            description: "AI adapts curriculum to each student's learning style and pace.",
            badge: 'AI-powered',
          },
          {
            icon: <Analytics size={32} />,
            title: 'Real-Time Analytics',
            description: 'Comprehensive insights into learning progress and performance.',
            badge: 'Live data',
          },
          {
            icon: <Collaborate size={32} />,
            title: 'Family Collaboration',
            description: 'Connect students, teachers, and parents seamlessly.',
            badge: 'All-in-one',
          },
          {
            icon: <Security size={32} />,
            title: 'Secure & Compliant',
            description: 'FERPA compliant, COPPA certified, and SOC 2 Type II certified.',
            badge: 'Enterprise-grade',
          },
        ];
    }
  };

  const getStatistics = (): Statistic[] => {
    switch (variant) {
      case 'educator':
        return [
          { value: '15,000+', label: 'Active Teachers', trend: '+42%' },
          { value: '5hrs/week', label: 'Time Saved', trend: 'avg' },
          { value: '94%', label: 'Student Engagement', trend: '+18%' },
          { value: '4.8/5', label: 'Teacher Rating', trend: 'stars' },
        ];
      case 'parent':
        return [
          { value: '50,000+', label: 'Connected Families', trend: '+67%' },
          { value: '99%', label: 'Parent Satisfaction', trend: 'rating' },
          { value: '85%', label: 'Homework Completion', trend: '+23%' },
          { value: '2min', label: 'Avg Response Time', trend: 'fast' },
        ];
      case 'administrator':
        return [
          { value: '200+', label: 'School Districts', trend: '+35%' },
          { value: '12%', label: 'Performance Improvement', trend: 'up' },
          { value: '30%', label: 'Admin Time Saved', trend: 'efficiency' },
          { value: '$2.3M', label: 'Avg Annual Savings', trend: 'ROI' },
        ];
      default:
        return [
          { value: '10,000+', label: 'Active Educators', trend: '+52%' },
          { value: '98%', label: 'Satisfaction Rate', trend: 'rating' },
          { value: '75%', label: 'Time Saved', trend: 'efficiency' },
          { value: '250k+', label: 'Students Impacted', trend: '+89%' },
        ];
    }
  };

  const getTestimonials = (): Testimonial[] => {
    switch (variant) {
      case 'educator':
        return [
          {
            quote:
              'Sigma08 has transformed how I teach. The AI lesson planning saves me hours every week, and my students are more engaged than ever.',
            author: 'Sarah Martinez',
            role: '5th Grade Teacher',
            organization: 'Lincoln Elementary School',
            rating: 5,
          },
          {
            quote:
              'The automated grading and instant feedback features have revolutionized my workflow. I can focus on what matters - teaching.',
            author: 'Michael Chen',
            role: 'High School Math Teacher',
            organization: 'Westfield High School',
            rating: 5,
          },
        ];
      case 'parent':
        return [
          {
            quote:
              "Finally, I can stay connected to my children's education without being overwhelmed. The real-time updates are invaluable.",
            author: 'Jennifer Thompson',
            role: 'Parent of 3',
            organization: 'Roosevelt Middle School',
            rating: 5,
          },
          {
            quote:
              'The AI tutoring support has been a game-changer for homework time. My daughter is more confident and independent.',
            author: 'David Rodriguez',
            role: 'Parent',
            organization: 'Oak Valley Elementary',
            rating: 5,
          },
        ];
      case 'administrator':
        return [
          {
            quote:
              "Sigma08's analytics have given us unprecedented visibility into district performance. We've improved outcomes by 12% in just one year.",
            author: 'Dr. Patricia Williams',
            role: 'Superintendent',
            organization: 'Metro School District',
            rating: 5,
          },
          {
            quote:
              'The compliance automation alone has saved us thousands of hours. ROI was evident within the first quarter.',
            author: 'Robert Johnson',
            role: 'Director of Technology',
            organization: 'Unified School District',
            rating: 5,
          },
        ];
      default:
        return [
          {
            quote: 'Sigma08 is the future of education. It brings together everything we need in one platform.',
            author: 'Lisa Anderson',
            role: 'Principal',
            organization: 'Riverside Academy',
            rating: 5,
          },
          {
            quote:
              'The platform has exceeded our expectations. Student engagement and outcomes have significantly improved.',
            author: 'James Wilson',
            role: 'Education Director',
            organization: 'State Board of Education',
            rating: 5,
          },
        ];
    }
  };

  const heroContent = getHeroContent();
  const features = getFeatures();
  const statistics = getStatistics();
  const testimonials = getTestimonials();

  return (
    <div className="marketing-landing-page">
      <MarketingHeader variant={variant} />
      <ConversionBreadcrumb variant={variant} />

      {/* Hero Section */}
      <section className="hero-section">
        <Grid className="hero-grid">
          <Column lg={16} md={8} sm={4}>
            <div className="hero-content">
              <h1 className="hero-headline">{heroContent.headline}</h1>
              <p className="hero-subheadline">{heroContent.subheadline}</p>

              <ButtonSet className="hero-buttons">
                <Button kind="primary" renderIcon={ArrowRight} onClick={() => (window.location.href = '/trial')}>
                  {heroContent.primaryCTA}
                </Button>
                <Button kind="tertiary" onClick={() => (window.location.href = '/value')}>
                  Explore Educational Value
                </Button>
              </ButtonSet>

              {/* Trust badges */}
              <div className="trust-badges">
                <Tag type="green">FERPA Compliant</Tag>
                <Tag type="blue">SOC 2 Type II</Tag>
                <Tag type="blue">COPPA Certified</Tag>
              </div>
            </div>
          </Column>
        </Grid>
      </section>

      {/* Statistics Section */}
      <section className="statistics-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div className="statistics-grid">
              {statistics.map((stat, index) => (
                <Tile key={index} className="statistic-tile">
                  <div className="statistic-value">{stat.value}</div>
                  <div className="statistic-label">{stat.label}</div>
                  {stat.trend && (
                    <Tag type="green" className="statistic-trend">
                      {stat.trend}
                    </Tag>
                  )}
                </Tile>
              ))}
            </div>
          </Column>
        </Grid>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <h2 className="section-title">
              {variant === 'educator'
                ? 'Tools Built for Teachers'
                : variant === 'parent'
                  ? 'Stay Connected & Informed'
                  : variant === 'administrator'
                    ? 'District-Wide Solutions'
                    : 'Powerful Features for Everyone'}
            </h2>

            <div className="features-grid">
              {features.map((feature, index) => (
                <ClickableTile key={index} className="feature-tile">
                  <div className="feature-icon">{feature.icon}</div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                  {feature.badge && (
                    <Tag type="cyan" className="feature-badge">
                      {feature.badge}
                    </Tag>
                  )}
                </ClickableTile>
              ))}
            </div>
          </Column>
        </Grid>
      </section>

      {/* Platform Benefits - StructuredList */}
      <section className="benefits-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <h2 className="section-title">Why Choose Sigma08</h2>
            <StructuredListWrapper ariaLabel="Platform benefits">
              <StructuredListHead>
                <StructuredListRow head>
                  <StructuredListCell head>Benefit</StructuredListCell>
                  <StructuredListCell head>Description</StructuredListCell>
                  <StructuredListCell head>Impact</StructuredListCell>
                </StructuredListRow>
              </StructuredListHead>
              <StructuredListBody>
                <StructuredListRow>
                  <StructuredListCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Education size={20} />
                      AI-Powered Learning
                    </div>
                  </StructuredListCell>
                  <StructuredListCell>Personalized learning paths adapted to each student's needs</StructuredListCell>
                  <StructuredListCell>
                    <Tag type="green">75% improvement</Tag>
                  </StructuredListCell>
                </StructuredListRow>
                <StructuredListRow>
                  <StructuredListCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Analytics size={20} />
                      Real-time Analytics
                    </div>
                  </StructuredListCell>
                  <StructuredListCell>Comprehensive insights into learning progress and performance</StructuredListCell>
                  <StructuredListCell>
                    <Tag type="blue">&lt;50ms response</Tag>
                  </StructuredListCell>
                </StructuredListRow>
                <StructuredListRow>
                  <StructuredListCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Security size={20} />
                      Enterprise Security
                    </div>
                  </StructuredListCell>
                  <StructuredListCell>FERPA compliant, COPPA certified, SOC 2 Type II certified</StructuredListCell>
                  <StructuredListCell>
                    <Tag type="blue">100% compliant</Tag>
                  </StructuredListCell>
                </StructuredListRow>
                <StructuredListRow>
                  <StructuredListCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Time size={20} />
                      Time Savings
                    </div>
                  </StructuredListCell>
                  <StructuredListCell>Automated grading and lesson planning save educators time</StructuredListCell>
                  <StructuredListCell>
                    <Tag type="cyan">5hrs/week saved</Tag>
                  </StructuredListCell>
                </StructuredListRow>
              </StructuredListBody>
            </StructuredListWrapper>
          </Column>
        </Grid>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <h2 className="section-title">What Our Users Say</h2>

            <div className="testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <Tile key={index} className="testimonial-tile">
                  <div className="testimonial-rating">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} />
                    ))}
                  </div>
                  <blockquote className="testimonial-quote">"{testimonial.quote}"</blockquote>
                  <div className="testimonial-author">
                    <strong>{testimonial.author}</strong>
                    <span>{testimonial.role}</span>
                    <span>{testimonial.organization}</span>
                  </div>
                </Tile>
              ))}
            </div>
          </Column>
        </Grid>
      </section>

      {/* Educational Calculator Hub Section */}
      <section className="calculator-hub-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div className="calculator-hub-intro">
              <h2 className="section-title">Calculate Your Educational Impact</h2>
              <p className="section-subtitle">
                Discover the measurable benefits of implementing Sigma08 in your institution. Our interactive
                calculators provide personalized insights based on your specific needs.
              </p>
            </div>

            <EducationalCalculatorHub
              visitorId={`visitor-${Date.now()}`}
              enableABTesting={true}
              onLeadCapture={leadData => {
                // Handle lead capture
                // TODO: Integrate with CRM/Marketing automation
              }}
              onDemoRequest={demoData => {
                // Handle demo request
                // TODO: Integrate with demo scheduling system
              }}
            />
          </Column>
        </Grid>
      </section>

      {/* FAQ Section with Accordion */}
      <section className="faq-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <h2 className="section-title">Frequently Asked Questions</h2>
            <Accordion>
              <AccordionItem title="How does AI-powered personalization work?">
                <p>
                  Our AI engine analyzes individual learning patterns, progress data, and engagement metrics to create
                  personalized learning paths. The system adapts in real-time, adjusting content difficulty, pacing, and
                  teaching methods to match each student's unique learning style.
                </p>
              </AccordionItem>
              <AccordionItem title="What are the privacy and security measures?">
                <p>
                  Sigma08 is FERPA compliant, COPPA certified, and SOC 2 Type II certified. We implement end-to-end
                  encryption, secure data storage, and comprehensive access controls. Student data privacy is our top
                  priority with transparent consent management.
                </p>
              </AccordionItem>
              <AccordionItem title="How quickly can we see results?">
                <p>
                  Most institutions see measurable improvements within 30-60 days. Key metrics include increased student
                  engagement (avg 75%), reduced teacher administrative time (5hrs/week), and improved learning outcomes.
                  Our implementation team ensures rapid deployment.
                </p>
              </AccordionItem>
              <AccordionItem title="What support is provided during implementation?">
                <p>
                  We provide comprehensive support including dedicated implementation specialists, professional
                  development training, 24/7 technical support, and ongoing optimization consulting. Our success team
                  works closely with your institution throughout the journey.
                </p>
              </AccordionItem>
              <AccordionItem title="Is Sigma08 suitable for all grade levels?">
                <p>
                  Yes, Sigma08 adapts to K-12, higher education, and corporate training environments. Our content
                  library and AI algorithms are designed to work across all age groups and subject areas, with
                  specialized modules for different educational contexts.
                </p>
              </AccordionItem>
            </Accordion>
          </Column>
        </Grid>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div className="cta-content">
              <h2 className="cta-title">
                Ready to Transform{' '}
                {variant === 'educator'
                  ? 'Your Classroom'
                  : variant === 'parent'
                    ? "Your Family's Education"
                    : variant === 'administrator'
                      ? 'Your District'
                      : 'Education'}
                ?
              </h2>
              <p className="cta-subtitle">
                Join thousands of{' '}
                {variant === 'educator'
                  ? 'teachers'
                  : variant === 'parent'
                    ? 'families'
                    : variant === 'administrator'
                      ? 'districts'
                      : 'educators'}{' '}
                already using Sigma08
              </p>

              <ButtonSet className="cta-buttons">
                <Button kind="primary" renderIcon={ArrowRight}>
                  {heroContent.primaryCTA}
                </Button>
                <Button kind="secondary" renderIcon={Calendar}>
                  Schedule a Demo
                </Button>
              </ButtonSet>

              <p className="cta-note">No credit card required • 30-day free trial • Cancel anytime</p>
            </div>
          </Column>
        </Grid>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default MarketingLandingPage;
